import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { websiteBannerControllerUpdateBannerById } from '@/generated/korza-api-client';
import type { WebsiteBannerDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TranslationFunctions } from '@/i18n/i18n-types';
import {
  getCouponFeatureState,
  getFeatureSummary,
  getWebsiteBannerFeatureState,
  getWelcomeFeatureState,
  toggledCouponDto,
  toggledWebsiteBannerBody,
  toggledWelcomeDto,
  type AppFeatureState,
  type FeatureSummary,
} from '@/lib/app-editor/app-hub';
import { couponDtoToForm } from '@/lib/app-editor/coupon-banner-form';
import {
  isWelcomeDelay,
  type DelayParts,
} from '@/lib/app-editor/welcome-notification-form';
import type { CouponBannerDto, WelcomeNotificationDto } from '@/lib/dtos';
import { cn } from '@/lib/utils';
import { DEFAULT_BANNER } from '@/lib/website-banner-constants';

import { AppFeatureCard } from '@/components/app-hub/app-feature-card';
import { AppHubHero } from '@/components/app-hub/app-hub-hero';
import {
  CartGlimpse,
  CouponGlimpse,
  SiteGlimpse,
  WelcomeGlimpse,
} from '@/components/app-hub/feature-glimpses';
import { useAppFeatureToggle } from '@/hooks/use-app-feature-toggle';
import {
  useGetCouponBanner,
  useSetCouponBanner,
} from '@/hooks/use-coupon-banner';
import { useGetWebsiteBanner } from '@/hooks/use-get-website-banner';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTenant } from '@/hooks/use-tenant';
import {
  useGetWelcomeNotification,
  useSetWelcomeNotification,
} from '@/hooks/use-welcome-notification';
import { SidebarLayout } from '@/layouts/sidebar.layout';

function formatDelay(delay: DelayParts, LL: TranslationFunctions): string {
  const options = LL.appCustomizationPage.welcomeNotifications.delayOptions;
  if (delay.kind === 'immediately') return options.immediately();
  const { amount } = delay;
  const unit = {
    minutes: options.minutes(),
    hours: amount === 1 ? options.hour() : options.hours(),
    days: amount === 1 ? options.day() : options.days(),
  }[delay.kind];
  return `${amount} ${unit}`;
}

function formatSummary(summary: FeatureSummary, LL: TranslationFunctions) {
  switch (summary.kind) {
    case 'notConfigured':
      return LL.appHub.notConfiguredHint();
    case 'coupon': {
      const position =
        summary.position === 'top'
          ? LL.appCustomizationPage.couponBanner.top()
          : LL.appCustomizationPage.couponBanner.bottom();
      return `${summary.couponCode} · ${position}`;
    }
    case 'welcome':
      return formatDelay(summary.delay, LL);
    case 'websiteBanner':
      return summary.buttonText;
  }
}

export default function AppCustomizationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const isMobile = useIsMobile();
  const layout = isMobile ? 'row' : 'tile';
  const editorPath = (segment: string) =>
    `/${tenant.slug}/customization/${segment}`;

  const couponTitle = LL.appCustomizationPage.couponBanner.title();
  const welcomeTitle = LL.appCustomizationPage.welcomeNotifications.title();
  const websiteTitle = LL.appCustomizationPage.websiteBanner.title();

  const coupon = useGetCouponBanner();
  const setCoupon = useSetCouponBanner();
  const welcome = useGetWelcomeNotification();
  const setWelcome = useSetWelcomeNotification();
  const websiteBanner = useGetWebsiteBanner();
  const bannerId = tenant.websiteBannerId ?? null;

  const couponState = getCouponFeatureState({
    status: coupon.status,
    data: coupon.data,
    appVersion: tenant.appVersion,
  });
  const welcomeState = getWelcomeFeatureState({
    status: welcome.status,
    data: welcome.data,
  });
  const websiteState = getWebsiteBannerFeatureState({
    websiteBannerId: bannerId,
    isStoreIntegrationConfigured: tenant.isStoreIntegrationConfigured,
    status: websiteBanner.status,
    data: websiteBanner.data,
  });

  const couponToggle = useAppFeatureToggle<CouponBannerDto>({
    queryKey: ['coupon-banner', tenant.slug],
    current: coupon.data,
    buildNext: toggledCouponDto,
    save: setCoupon.mutateAsync,
    errorMessage: LL.appHub.toggleError({ title: couponTitle }),
    onNotConfigured: () => navigate(editorPath('coupon-banner')),
  });
  const welcomeToggle = useAppFeatureToggle<WelcomeNotificationDto>({
    queryKey: ['welcome-notification', tenant.slug],
    current: welcome.data,
    buildNext: toggledWelcomeDto,
    save: setWelcome.mutateAsync,
    errorMessage: LL.appHub.toggleError({ title: welcomeTitle }),
    onNotConfigured: () => navigate(editorPath('welcome-notification')),
  });
  const websiteCurrent = bannerId === null ? null : websiteBanner.data;
  const websiteToggle = useAppFeatureToggle<WebsiteBannerDto>({
    queryKey: ['website-banner', bannerId],
    current: websiteCurrent,
    buildNext: (dto) => ({ ...dto, enabled: !dto.enabled }),
    save: async () => {
      if (!bannerId || !websiteCurrent) throw new Error('No website banner');
      const res = await websiteBannerControllerUpdateBannerById({
        path: { bannerId },
        body: toggledWebsiteBannerBody(websiteCurrent),
      });
      if (res.data == null) throw new Error('Update failed');
      await queryClient.invalidateQueries({ queryKey: ['website-banner'] });
    },
    errorMessage: LL.appHub.toggleError({ title: websiteTitle }),
    onNotConfigured: () => navigate(editorPath('website-banner')),
  });

  function summaryOf(
    state: AppFeatureState,
    summary: FeatureSummary | null
  ): string {
    const showsSummary =
      state.kind === 'configured' || state.kind === 'notConfigured';
    if (!showsSummary || !summary) return '';
    return formatSummary(summary, LL);
  }

  // What each thumbnail draws: the saved config, or a sample before there is one.
  const couponForm = coupon.data ? couponDtoToForm(coupon.data) : null;
  const welcomeData = welcome.data ?? null;
  const siteBanner = websiteCurrent ?? DEFAULT_BANNER;
  const compact = isMobile;

  return (
    <SidebarLayout
      mobileHeader={{ title: LL.appHub.title() }}
      className="bg-[#F9F9F9] text-[#15191E] max-md:px-5 max-md:pt-0 md:px-12 md:pb-14 md:pt-12 lg:px-16"
    >
      <div className={cn('flex flex-col', isMobile ? 'gap-3.5' : 'gap-10')}>
        <AppHubHero tenant={tenant} compact={isMobile} />

        <section
          aria-label={LL.appHub.features()}
          className="flex flex-col gap-3 md:gap-[18px]"
        >
          <div className="flex items-center gap-3.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B6371]">
            <span>{LL.appHub.features()}</span>
            <span aria-hidden className="h-px flex-1 bg-[#15191E]/[.08]" />
            <span>04</span>
          </div>

          <div
            className={cn(
              isMobile
                ? 'flex flex-col gap-3 pb-6'
                : 'grid grid-cols-2 gap-4 min-[1360px]:grid-cols-4'
            )}
          >
            <AppFeatureCard
              number="01"
              where={LL.appHub.where.app()}
              title={couponTitle}
              layout={layout}
              state={couponState}
              summary={summaryOf(
                couponState,
                getFeatureSummary('couponBanner', coupon.data ?? null)
              )}
              explanation={
                couponState.kind === 'waitingForUpdate'
                  ? LL.appCustomizationPage.waitingForUpdateDescription()
                  : undefined
              }
              glimpse={
                <CouponGlimpse
                  storeName={tenant.name}
                  iconUrl={tenant.iconUrl}
                  banner={couponForm}
                  compact={compact}
                />
              }
              isPending={couponToggle.isPending}
              onToggle={couponToggle.toggle}
              onOpen={() => navigate(editorPath('coupon-banner'))}
              onRetry={() => void coupon.refetch()}
            />
            <AppFeatureCard
              number="02"
              where={LL.appHub.where.lockScreen()}
              title={welcomeTitle}
              layout={layout}
              state={welcomeState}
              summary={summaryOf(
                welcomeState,
                getFeatureSummary('welcomeNotification', welcome.data ?? null)
              )}
              glimpse={
                <WelcomeGlimpse
                  title={welcomeData?.title ?? ''}
                  body={welcomeData?.body ?? ''}
                  iconUrl={tenant.iconUrl}
                  delayMinutes={
                    welcomeData && isWelcomeDelay(welcomeData.delayMinutes)
                      ? welcomeData.delayMinutes
                      : 0
                  }
                  compact={compact}
                />
              }
              isPending={welcomeToggle.isPending}
              onToggle={welcomeToggle.toggle}
              onOpen={() => navigate(editorPath('welcome-notification'))}
              onRetry={() => void welcome.refetch()}
            />
            <AppFeatureCard
              number="03"
              where={LL.appHub.where.site()}
              title={websiteTitle}
              layout={layout}
              state={websiteState}
              summary={summaryOf(
                websiteState,
                getFeatureSummary('websiteBanner', websiteCurrent ?? null)
              )}
              glimpse={
                <SiteGlimpse
                  iconUrl={tenant.iconUrl}
                  domain={tenant.storeWebsiteDomain}
                  content={siteBanner.content}
                  theme={siteBanner.theme}
                  compact={compact}
                />
              }
              isPending={websiteToggle.isPending}
              onToggle={websiteToggle.toggle}
              onOpen={() => navigate(editorPath('website-banner'))}
              onRetry={() => void websiteBanner.refetch()}
            />
            <AppFeatureCard
              number="04"
              where={LL.appHub.where.lockScreen()}
              title={LL.appCustomizationPage.abandonedCartNotifications.title()}
              layout={layout}
              state={{ kind: 'comingSoon' }}
              summary={LL.appCustomizationPage.abandonedCartNotifications.description()}
              glimpse={<CartGlimpse compact={compact} />}
              isPending={false}
              onToggle={() => undefined}
            />
          </div>
        </section>
      </div>
    </SidebarLayout>
  );
}
